import { AppLink } from "@base/ui/AppLink";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { projectDetailQueryOptions } from "../model/useProjectDetailQuery";

interface ProjectPageHeaderProps {
  projectId: string;
  /** 모바일 header 제목 (예: "Projects", "Work History") */
  mobileTitle: string;
  /** 모바일 뒤로 가기 대상. route 이동은 page가 정한다 */
  mobileBackLink: ReactNode;
  /** PC header 오른쪽 동작 (예: New Daily Log) */
  actions?: ReactNode;
}

/**
 * 프로젝트 화면 공통 header.
 * - 모바일: "‹ 제목" header
 * - 768px 이상: Projects / 이름 breadcrumb, 프로젝트 이름, 주소
 * 프로젝트 정보가 늦게 와도 화면 전체를 멈추지 않도록 일반 query를 쓴다.
 */
export function ProjectPageHeader({ projectId, mobileTitle, mobileBackLink, actions }: ProjectPageHeaderProps) {
  const { data: project } = useQuery(projectDetailQueryOptions(projectId));

  return (
    <header>
      <div className="flex gap-2 items-center md:hidden">
        {mobileBackLink}
        <h1 className="text-title5 text-gray-1000">{mobileTitle}</h1>
      </div>

      <div className="hidden gap-4 justify-between items-end pt-6 pb-2 md:flex">
        <div className="flex flex-col gap-1 min-w-0">
          <nav aria-label="Breadcrumb" className="text-label3 text-gray-600">
            <ol className="flex gap-1 items-center">
              <li>
                <AppLink to="/projects" direction="none" className="hover:underline">
                  Projects
                </AppLink>
              </li>
              <li aria-hidden="true">/</li>
              <li className="truncate" aria-current="page">
                {project?.name ?? "…"}
              </li>
            </ol>
          </nav>
          <h1 className="truncate text-title5 text-gray-1000" title={project?.name}>
            {project?.name ?? <span className="inline-block w-40 h-6 align-middle rounded-2 skeleton-shimmer" />}
          </h1>
          {project?.address && <p className="truncate text-body4 text-gray-700">{project.address}</p>}
        </div>
        {actions && <div className="flex gap-2 shrink-0">{actions}</div>}
      </div>
    </header>
  );
}
